import {
  Button,
  Icon,
  Input,
  Modal,
  PageHeader,
  Select,
  Table,
  TableColumn,
} from '@components';
import { useModal } from '@hooks';
import { useConfirmationModal } from '@hooks/useConfirmationModal';
import CategoryAPICaller, { CategoryFilters } from '@services/api/category';
import CostCenterAPICaller from '@services/api/costCenter';
import { Category } from 'interfaces/Category';
import { CostCenter } from 'interfaces/CostCenter';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { usePermissionContext } from '@contexts/PermissionProvider';

const ESTIMATE_SOURCE_OPTIONS = [
  { label: 'Nenhuma (categoria manual)', value: '' },
  { label: 'Remuneração (Produção)', value: 'compensation' },
  { label: 'Plano de saúde', value: 'health_plan' },
];

export default function CategoryPage() {
  const [tableData, setTableData] = useState<{
    total: number;
    result: Array<Category>;
  }>({
    total: 0,
    result: [],
  });
  const [filters, setFilters] = useState<CategoryFilters>({});

  const [isLoading, setIsLoading] = useState(false);

  const { hasPermission } = usePermissionContext();

  const [costCenters, setCostCenters] = useState<Array<CostCenter>>([]);
  const { isOpen, toggle: toggleCategoryModal } = useModal();
  const confirmation = useConfirmationModal();
  const {
    watch,
    register,
    setError,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const loadCostCenters = useCallback(() => {
    if (hasPermission('costCenters', 'read')) {
      CostCenterAPICaller.listAll().then((values) => {
        setCostCenters(
          values.result.map((costCenter: CostCenter) => {
            return {
              label: costCenter.name,
              value: costCenter.id,
            };
          })
        );
      });
    }
  }, [hasPermission]);

  useEffect(() => {
    loadCostCenters();
  }, [loadCostCenters]);

  useEffect(() => {
    loadTableData(filters);
  }, [filters]);

  const loadTableData = (filters: CategoryFilters) => {
    setIsLoading(true);
    CategoryAPICaller.list(filters)
      .then((values) => {
        setTableData(values);
      })
      .finally(() => setIsLoading(false));
  };

  const openEditModal = async (editData: any) => {
    const category = await CategoryAPICaller.fetch(editData);
    setValue('id', category.id);
    setValue('name', category.name);
    setValue('costCenter', {
      label: category.costCenter.name,
      value: category.costCenter.id,
    });
    setValue('costCenterId', category.costCenter.id);
    setValue('estimateSource', category.estimateSource ?? '');
    setValue(
      'estimateSourceOption',
      ESTIMATE_SOURCE_OPTIONS.find(
        (option) => option.value === (category.estimateSource ?? '')
      )
    );
    toggleCategoryModal();
  };

  const handleDelete = async (categoryId: string) => {
    confirmation({
      title: 'Excluir categoria',
      confirmButtonTitle: 'Excluir',
      description: 'Deseja realmente excluir essa categoria?',
      onSubmit: () =>
        CategoryAPICaller.remove(categoryId)
          .then((_) => loadTableData(filters))
          .catch((error) => {
            const message = error.response.data.message || 'Erro ao excluir!';
            toast.error(message);
          }),
    });
  };

  const handleCreateOrUpdate = async (data: Category) => {
    CategoryAPICaller.createOrUpdate(data, setError).then((res) => {
      if (res.data.errors) return;
      toggleCategoryModal();
      loadTableData(filters);
    });
  };

  return (
    <>
      <div className="p-s-300">
        <PageHeader title="Categorias" />

        <div className="mt-s-200">
          <Table
            additionalFilters={
              <div className="d-flex justify-end">
                <Button
                  className="ml-s-100"
                  suffixes={<Icon>add</Icon>}
                  design="filled"
                  onClick={() => {
                    setValue('id', '');
                    setValue('costCenter', undefined);
                    setValue('costCenterId', '');
                    setValue('name', '');
                    setValue('estimateSource', '');
                    setValue(
                      'estimateSourceOption',
                      ESTIMATE_SOURCE_OPTIONS[0]
                    );
                    return toggleCategoryModal();
                  }}
                >
                  Nova categoria
                </Button>
              </div>
            }
            data={tableData.result}
            totalRecords={tableData.total}
            showColumnSelector={false}
            withPagination
            withSearchInput
            isLoading={isLoading}
            filters={filters}
            setFilters={setFilters}
          >
            <TableColumn fromKey="name" header="Categoria" />
            <TableColumn
              fromKey="costCenter.name"
              header="Centro de custo"
              render={(costCenter) => costCenter}
            />
            <TableColumn
              fromKey="costCenter.department"
              header="Departamento"
              render={(costCenter) => costCenter}
            />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Editar"
              disableSorting
              render={(id) => {
                return (
                  <Button
                    design="transparent"
                    prefixes={<Icon>edit</Icon>}
                    onClick={() => {
                      openEditModal(id);
                    }}
                  />
                );
              }}
            />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Deletar"
              disableSorting
              render={(id) => {
                return (
                  <Button
                    design="transparent"
                    prefixes={<Icon>delete</Icon>}
                    onClick={async () => handleDelete(id)}
                  />
                );
              }}
            />
          </Table>
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        onClickAway={toggleCategoryModal}
        size="extra-small"
        title="Cadastrar categoria"
      >
        <form
          onSubmit={handleSubmit((data) =>
            handleCreateOrUpdate(data as Category)
          )}
        >
          <div className="mb-s-200">
            <Input
              label="Categoria"
              error={!!errors.name}
              caption={errors.name?.message as string}
              placeholder="Digite uma categoria"
              form={register('name', { required: 'Obrigatório' })}
            />
          </div>
          <div className="mb-s-200">
            <Select
              label="Centro de custo"
              value={watch('costCenter')}
              error={!!errors.costCenterId}
              caption={errors.costCenterId?.message as string}
              placeholder="Selecione um centro de custo"
              form={register('costCenterId', { required: 'Obrigatório' })}
              onSelect={(costCenter: any) => {
                setValue('costCenterId', costCenter.value);
                setValue('costCenter', costCenter);
              }}
              options={costCenters}
            />
          </div>
          <div className="mb-s-400">
            <Select
              label="Origem da estimativa"
              value={watch('estimateSourceOption')}
              caption="Alimentado pela estimativa de folha"
              placeholder="Nenhuma (categoria manual)"
              form={register('estimateSource')}
              onSelect={(option: any) => {
                setValue('estimateSource', option.value);
                setValue('estimateSourceOption', option);
              }}
              options={ESTIMATE_SOURCE_OPTIONS}
            />
          </div>
          <Button className="!w-100" type="submit" alignText="center">
            Salvar
          </Button>
        </form>
      </Modal>
    </>
  );
}
